import type { ExamDomain } from '@mathflat/domain/@entities/(Content)/Exam/domain'
import { CONTENT_STATUS } from '@mathflat/domain/@entities/(Content)/module'
import { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain.ts'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { Entity as WorksheetEntity } from '@mathflat/domain/@entities/(Content)/Worksheet/dto'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'
import { makeAutoObservable } from 'mobx'

import type { ProblemScoring } from './ProblemScoring.trait'

/** ViewOption 메서드들이 ProblemScoring에서 읽는 최소 표면. 테스트 mock·타입 정합용 */
export type ProblemScoringForViewOption = Pick<
  ProblemScoring<'WORKSHEET' | 'WORKBOOK'>,
  'isSubmitted' | 'isAutoScoring' | '문제해설이미지'
>

export type ProblemScoringViewOptionParams<T extends '학습모듈' | 'NOT_학습모듈'> = {
  content:
    | {
        status: ValueOf<typeof CONTENT_STATUS>
        autoScored: boolean
        kind: 'WORKSHEET'
        type?: WorksheetEntity.Worksheet['type']
        worksheetId: WorksheetEntity.Worksheet['id']
        examScheduleProgress?: ExamDomain.ScheduleProgress
      }
    | {
        status: ValueOf<typeof CONTENT_STATUS>
        autoScored: boolean
        kind: 'WORKBOOK'
        type: WorkbookEntity.Workbook['type']
      }
    | {
        status: ValueOf<typeof CONTENT_STATUS>
        autoScored: boolean
        kind: 'EXAM'
        type?: WorksheetEntity.Worksheet['type']
        worksheetId: WorksheetEntity.Worksheet['id']
        examScheduleProgress?: ExamDomain.ScheduleProgress
      }
  studentAppSetting: T extends 'NOT_학습모듈'
    ? {
        일반채점_채점전정답공개: boolean
        채점후정답해설공개: boolean
        채점전문풀동공개: boolean
        채점후문풀동공개: boolean
        /** 학부모 모드에서 채점후정답해설공개 로 매핑됨. 학생 모드에서는 미사용. */
        학부모정답해설공개?: boolean
      }
    : null
  viewerMode?: 'student' | 'parent'
  /** restriction 으로 풀이동영상이 차단 - AppSettings(채점전/후문풀동공개) 보다 우선 */
  videoRestricted?: boolean
  /** restriction 으로 학생 본인 필기 ("내풀이") 가 차단 */
  handwrittenRestricted?: boolean
  /** restriction 으로 선생님 필기가 차단 */
  teacherHandwrittenRestricted?: boolean
  문제같이보기?: boolean
  오답_모르는문제만보기?: boolean
  안푼문제만보기?: boolean
}

// 코드리뷰 내용 - 추후 학습모듈/NOT_학습모듈 외의 분기가 생길 경우, 제네릭 제거 후 interface로 빼고, 각각의 클래스로 분리
export class ProblemScoringViewOption<T extends '학습모듈' | 'NOT_학습모듈'> {
  content: {
    status: ValueOf<typeof CONTENT_STATUS>
    autoScored: boolean
    kind: 'WORKSHEET' | 'WORKBOOK' | 'EXAM'
    type?: WorksheetEntity.Worksheet['type'] | WorkbookEntity.Workbook['type']
    worksheetId?: WorksheetEntity.Worksheet['id']
    examScheduleProgress?: ExamDomain.ScheduleProgress
  }
  studentAppSetting: T extends 'NOT_학습모듈'
    ? {
        일반채점_채점전정답공개: boolean
        채점후정답해설공개: boolean
        채점전문풀동공개: boolean
        채점후문풀동공개: boolean
      }
    : null
  isParentView: boolean
  isVideoRestricted: boolean
  isHandwrittenRestricted: boolean
  isTeacherHandwrittenRestricted: boolean
  문제같이보기 = true
  오답_모르는문제만보기 = false
  안푼문제만보기 = false

  constructor(params: ProblemScoringViewOptionParams<T>) {
    makeAutoObservable(this)
    this.content = params.content
    const isParent = !!params.studentAppSetting && params.viewerMode === 'parent'
    this.isParentView = isParent
    this.isVideoRestricted = !!params.videoRestricted
    this.isHandwrittenRestricted = !!params.handwrittenRestricted
    this.isTeacherHandwrittenRestricted = !!params.teacherHandwrittenRestricted
    // 1) 학부모 모드(풀기 금지모드)면 학부모 정책으로 변환, 아니면 학생 setting 그대로
    const baseSetting: ProblemScoringViewOptionParams<T>['studentAppSetting'] =
      isParent && params.studentAppSetting
        ? ({
            일반채점_채점전정답공개: false,
            채점후정답해설공개: params.studentAppSetting.학부모정답해설공개 ?? false,
            채점전문풀동공개: false,
            채점후문풀동공개: false,
          } as ProblemScoringViewOptionParams<T>['studentAppSetting'])
        : params.studentAppSetting
    // 2) restriction 우선 적용 — 풀이동영상 차단 시 채점전/후문풀동공개 모두 false 로 덮어씀
    this.studentAppSetting =
      params.videoRestricted && baseSetting
        ? ({
            ...baseSetting,
            채점전문풀동공개: false,
            채점후문풀동공개: false,
          } as ProblemScoringViewOptionParams<T>['studentAppSetting'])
        : baseSetting
    this.문제같이보기 = params.문제같이보기 ?? false
    this.오답_모르는문제만보기 = params.오답_모르는문제만보기 ?? false
    this.안푼문제만보기 = params.안푼문제만보기 ?? false
  }

  static isScorable<T extends '학습모듈' | 'NOT_학습모듈'>(
    content: Pick<ProblemScoringViewOptionParams<T>['content'], 'autoScored' | 'status'>,
    studentAppSetting: ProblemScoringViewOptionParams<T>['studentAppSetting'],
  ) {
    if (studentAppSetting) {
      if (!content.autoScored) {
        if (content.status !== CONTENT_STATUS.전체채점) {
          return studentAppSetting.일반채점_채점전정답공개
        }
      }
    }
    return true
  }

  get 채점불가능() {
    return !ProblemScoringViewOption.isScorable(this.content, this.studentAppSetting)
  }

  get 채점완료() {
    return this.content.status === CONTENT_STATUS.전체채점
  }

  get 채점완료_혹은_채점불가능() {
    return this.채점완료 || this.채점불가능
  }

  /** 시험 응시 기간이 진행 중 — 응시 종료(RESOLVED)되기 전 상태 */
  get isExamInProgress() {
    return this.content.kind === 'EXAM' && this.content.examScheduleProgress !== 'RESOLVED'
  }

  /** 자기주도학습(학습모듈) 여부 — studentAppSetting이 없으면 true */
  get isSelfLearning() {
    return !this.studentAppSetting
  }

  get hasCopyRight() {
    if (this.content.kind === 'WORKBOOK') {
      switch (this.content.type) {
        case WorkbookDomain.TYPE.시중교재:
        case WorkbookDomain.TYPE.교과서:
        case WorkbookDomain.TYPE.커스텀시그니처교재:
        case WorkbookDomain.TYPE.학원그룹교재:
          return true
        default:
          return false
      }
    }
  }

  get 오답_모르는문제만보기_노출여부() {
    return this.content.status !== CONTENT_STATUS.채점전
  }

  get 안푼문제만보기_노출여부() {
    return (
      this.content.autoScored && !this.채점불가능 && this.content.status !== CONTENT_STATUS.전체채점
    )
  }

  get 문제같이보기_노출여부() {
    return !this.hasCopyRight
  }

  get 가이드_노출() {
    if (
      !this.채점불가능 &&
      this.content.status !== CONTENT_STATUS.전체채점 &&
      !this.content.autoScored
    ) {
      return '가이드_일반채점'
    }
    if (
      this.content.status !== CONTENT_STATUS.채점전 &&
      !this.studentAppSetting?.채점후정답해설공개
    ) {
      return '가이드_비공개'
    }
  }

  onToggle(
    params: Partial<{
      문제같이보기: boolean
      오답_모르는문제만보기: boolean
      안푼문제만보기: boolean
    }>,
  ) {
    const _params = {
      문제같이보기: this.문제같이보기,
      오답_모르는문제만보기: this.오답_모르는문제만보기,
      안푼문제만보기: this.안푼문제만보기,
    }

    Object.assign(_params, params)

    this.문제같이보기 = _params.문제같이보기
    this.오답_모르는문제만보기 = _params.오답_모르는문제만보기
    this.안푼문제만보기 = _params.안푼문제만보기
  }

  changeStudentWorksheetStatus(status: ValueOf<typeof CONTENT_STATUS>) {
    this.content.status = status
  }
}
